// SPDX-License-Identifier: FSL-1.1-Apache-2.0
/**
 * @module Timeline
 * @package @geenius/analytics/react
 * @description Renders the React analytics timeline for recent event activity.
 * The component maps shared timeline events into a compact log view with event
 * icons, optional user identifiers, and localized timestamps.
 */

import { EVENT_ICONS, type TimelineEvent } from "@geenius/analytics";
import { Card, CardContent } from "@geenius/ui/react";
import type React from "react";
import { useAnalyticsCopy } from "../lib/analyticsCopy";

/**
 * Props for rendering the React analytics timeline.
 *
 * @property events Timeline events to render in descending recency order.
 * @property maxVisible Maximum number of timeline rows to display.
 * @property className Optional class name merged into the timeline wrapper.
 */
export interface TimelineProps {
  events: TimelineEvent[];
  maxVisible?: number;
  className?: string;
}

/**
 * Renders a React timeline for recent analytics events.
 *
 * @param props Timeline events and the optional visible-row limit.
 * @returns A timeline log element for analytics activity.
 */
export function Timeline(props: TimelineProps): React.ReactElement {
  const copy = useAnalyticsCopy();
  const visible = props.events.slice(0, props.maxVisible ?? 20);

  return (
    <Card className={props.className}>
      <CardContent className="pt-6">
        <div
          className="flex flex-col gap-2"
          data-analytics="timeline"
          aria-label={copy.t("analytics.dashboard.recentEvents")}
          role="log"
        >
          {visible.map((event) => (
            <div
              key={event.id}
              className="flex items-center gap-3 rounded-xl bg-muted px-3 py-3"
              data-analytics="timeline-event"
            >
              <span aria-hidden="true" data-analytics="timeline-icon">
                {EVENT_ICONS[event.event] ?? "⚡"}
              </span>
              <span className="font-medium" data-analytics="timeline-name">
                {event.event}
              </span>
              {event.userId && (
                <span
                  className="text-sm text-muted-foreground"
                  data-analytics="timeline-user"
                >
                  {event.userId}
                </span>
              )}
              <span
                className="ml-auto text-sm text-muted-foreground"
                data-analytics="timeline-time"
              >
                {new Date(event.timestamp).toLocaleTimeString()}
              </span>
            </div>
          ))}
        </div>
      </CardContent>
    </Card>
  );
}
